import { useState, useRef } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { X, Paperclip, CheckCircle2, Loader2, Lightbulb } from "lucide-react";

const TYPES = [
  { value: "suggestion", label: "Suggestion" },
  { value: "bug", label: "Bug" },
  { value: "idee", label: "Idée" },
] as const;

type FeedbackType = typeof TYPES[number]["value"];

interface PortailFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PortailFeedbackModal({ isOpen, onClose }: PortailFeedbackModalProps) {
  const { portailUser, session } = usePortailAuth();
  const [type, setType] = useState<FeedbackType>("suggestion");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const auteurNom = `${portailUser?.prenom ?? ""} ${portailUser?.nom ?? ""}`.trim() || portailUser?.email || "";

  const handleClose = () => {
    if (isSubmitting) return;
    setMessage("");
    setFile(null);
    setIsDone(false);
    setError(null);
    setType("suggestion");
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) {
      setError("Format non autorisé. Accepté : PNG, JPEG, WebP, PDF.");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setError("Fichier trop lourd (max 50 Mo).");
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!message.trim()) { setError("Le message est obligatoire."); return; }
    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("message", message.trim());
      formData.append("contexte_url", window.location.pathname);
      if (file) formData.append("fichier", file);

      const res = await fetch("/api/portail/feedback", {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Erreur lors de l'envoi.");
      }

      setIsDone(true);
      setTimeout(() => handleClose(), 2200);
    } catch (e: any) {
      setError(e?.message || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-primary" />
            <h2 className="text-base font-semibold text-foreground">Faire une suggestion</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            data-testid="btn-feedback-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isDone ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
            <p className="font-semibold text-foreground">Merci !</p>
            <p className="text-sm text-muted-foreground">Votre retour a bien été transmis à l'équipe Jobotto.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Partagez vos idées, signalez un problème ou faites-nous part d'une suggestion d'amélioration.
            </p>

            {/* Type selector */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Type</label>
              <div className="flex gap-2" data-testid="portail-feedback-type-selector">
                {TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    disabled={isSubmitting}
                    data-testid={`portail-btn-type-${t.value}`}
                    className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      type === t.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Nom (pré-rempli, désactivé) */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Nom</label>
              <input
                value={auteurNom}
                disabled
                className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-muted-foreground text-sm cursor-not-allowed"
                data-testid="portail-input-feedback-nom"
              />
            </div>

            {/* Message */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Votre message <span className="text-destructive">*</span>
              </label>
              <textarea
                placeholder="Décrivez votre suggestion, problème ou idée..."
                value={message}
                onChange={(e) => { setMessage(e.target.value); setError(null); }}
                rows={5}
                disabled={isSubmitting}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
                data-testid="portail-textarea-feedback-message"
              />
            </div>

            {/* Pièce jointe */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Pièce jointe <span className="text-xs text-muted-foreground">(optionnel)</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                className="hidden"
                onChange={handleFileChange}
                data-testid="portail-input-feedback-file"
              />
              {file ? (
                <div className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-secondary/50 text-sm">
                  <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate text-foreground">{file.name}</span>
                  <button
                    onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    data-testid="portail-btn-remove-attachment"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors disabled:opacity-50"
                  data-testid="portail-btn-add-attachment"
                >
                  <Paperclip className="w-4 h-4" />
                  Joindre un fichier
                </button>
              )}
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg" data-testid="portail-feedback-error">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors disabled:opacity-50"
                data-testid="portail-btn-feedback-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !message.trim()}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="portail-btn-feedback-send"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi…
                  </>
                ) : "Envoyer"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
